import React from 'react';
import { MapPin, Star, TrendingUp, Users, DollarSign, Building2 } from 'lucide-react';

interface LocationCardProps {
  name: string;
  address: string;
  rating: number;
  potential: string;
  image: string;
  metrics: {
    footTraffic: string;
    competition: string;
    growth: string;
    demographics: {
      age: string;
      income: string;
      education: string;
    };
    marketSize: string;
    businessTypes: string[];
    nearbyAttractions: string[];
    propertyDetails: {
      size: string;
      rent: string;
      type: string;
      parking: string;
    };
  };
}

const LocationCard: React.FC<LocationCardProps> = ({
  name,
  address,
  rating,
  potential,
  image,
  metrics
}) => {
  return (
    <div className="bg-card rounded-lg overflow-visible border border-border hover:border-primary/50 transition-colors">
      <div className="aspect-video relative">
        <img src={image} alt={name} className="w-full h-full object-cover" />
        <div className="absolute top-2 right-2 bg-black/50 backdrop-blur-sm text-white px-2 py-1 rounded-full text-sm">
          {metrics.propertyDetails.size}
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-lg font-semibold mb-2">{name}</h3>
        <div className="flex items-center text-sm text-muted-foreground mb-2">
          <div className="group relative">
            <MapPin size={16} className="mr-1" />
            <div className="invisible group-hover:visible absolute -top-2 left-6 transform -translate-y-full px-3 py-2 bg-popover/90 backdrop-blur-xl border border-border shadow-lg text-popover-foreground text-xs rounded-lg whitespace-nowrap">
              Physical location of the property
            </div>
          </div>
          {address}
        </div>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="flex items-center">
            <div className="group relative">
              <Star size={16} className="text-primary mr-1" />
              <div className="invisible group-hover:visible absolute -top-2 left-6 transform -translate-y-full px-3 py-2 bg-popover/90 backdrop-blur-xl border border-border shadow-lg text-popover-foreground text-xs rounded-lg whitespace-nowrap">
                Overall rating based on location quality and potential
              </div>
            </div>
            <span>{rating}/5</span>
          </div>
          <div className="flex items-center text-accent">
            <div className="group relative">
              <TrendingUp size={16} className="mr-1" />
              <div className="invisible group-hover:visible absolute -top-2 left-6 transform -translate-y-full px-3 py-2 bg-popover/90 backdrop-blur-xl border border-border shadow-lg text-popover-foreground text-xs rounded-lg whitespace-nowrap">
                Projected business growth in this area
              </div>
            </div>
            <span>{potential}</span>
          </div>
          <div className="flex items-center">
            <div className="group relative">
              <DollarSign size={16} className="mr-1" />
              <div className="invisible group-hover:visible absolute -top-2 left-6 transform -translate-y-full px-3 py-2 bg-popover/90 backdrop-blur-xl border border-border shadow-lg text-popover-foreground text-xs rounded-lg whitespace-nowrap">
                Monthly rental cost for this property
              </div>
            </div>
            <span>{metrics.propertyDetails.rent}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center text-muted-foreground">
            <div className="group relative">
              <Users size={14} className="mr-1" />
              <div className="invisible group-hover:visible absolute -top-2 left-6 transform -translate-y-full px-3 py-2 bg-popover/90 backdrop-blur-xl border border-border shadow-lg text-popover-foreground text-xs rounded-lg whitespace-nowrap">
                Average number of people passing by daily
              </div>
            </div>
            <span>{metrics.footTraffic}</span>
          </div>
          <div className="flex items-center text-muted-foreground">
            <div className="group relative">
              <Building2 size={14} className="mr-1" />
              <div className="invisible group-hover:visible absolute -top-2 left-6 transform -translate-y-full px-3 py-2 bg-popover/90 backdrop-blur-xl border border-border shadow-lg text-popover-foreground text-xs rounded-lg whitespace-nowrap">
                Level of similar businesses in the area
              </div>
            </div>
            <span>{metrics.competition} Competition</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationCard;